<template>
  <fieldset :id="id" class="parcel-config-options">
    <legend class="visually-hidden">
      {{ t('common.paramConfigOptions', { param: t('enumMap.Mode.PARCEL') }) }}
    </legend>

    <InputWrapper
      v-slot="{ ariaProps, inputId, invalid }"
      :input-id="`${id}-label-size`"
      :label="t('rateShop.assignAndTenderModal.labelSizes')"
      :errors="v$.parcelLabelSize.$errors"
    >
      <RygenDropdown
        :id="inputId"
        v-model="tenderConfigOptions.parcelLabelSize"
        v-bind="ariaProps"
        :options="parcelShippingLabelFormatOptions"
        :invalid="invalid"
        :loading="defaultParcelLabelSize.loading"
        :option-label="(option) => optionLabelParcelLabelSize(option)"
        option-value="value"
        append-to="body"
        filter
        :placeholder="t('rateShop.assignAndTenderModal.labelSizes')"
      />
    </InputWrapper>
  </fieldset>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { Ref } from 'vue';
import { onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import {
  DropdownOption,
  Loadable,
  ParcelShippingLabelFormat,
  TenderConfigOptions,
} from '@rygen/rygen-ui/model';

import api from '@/api';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const tenderConfigOptions = defineModel({
  type: TenderConfigOptions,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});
const { id } = toRefs(props);

const { t } = useI18n();

const rules = {
  parcelLabelSize: {
    required: required(t('rateShop.assignAndTenderModal.labelSizes')),
  },
};
const { v$ } = useValidations(tenderConfigOptions, rules);

const parcelShippingLabelFormatOptions: DropdownOption<string>[] =
  mapEnumToTranslatedDropdownValues(
    ParcelShippingLabelFormat,
    'ParcelShippingLabelFormat',
  );

const defaultParcelLabelSize: Ref<Loadable<ParcelShippingLabelFormat>> = ref({
  loading: false,
});

const optionLabelParcelLabelSize = (option: DropdownOption<string>) => {
  const isDefaultLabelSize = option.value === defaultParcelLabelSize.value.data;

  return isDefaultLabelSize
    ? t('common.paramDefault', { param: option.label })
    : option.label;
};

const getClientShippingLabelSettings = async () => {
  try {
    defaultParcelLabelSize.value.loading = true;
    const labelSettings = await api.client.getClientShippingLabelSettings();

    if (labelSettings.defaultParcelLabelSize) {
      defaultParcelLabelSize.value.data = labelSettings.defaultParcelLabelSize;

      if (!tenderConfigOptions.value.parcelLabelSize) {
        tenderConfigOptions.value.parcelLabelSize =
          labelSettings.defaultParcelLabelSize;
      }
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.warn('Error loading default parcel label size', e);
    defaultParcelLabelSize.value.error = e;
  } finally {
    defaultParcelLabelSize.value.loading = false;
  }
};

onMounted(() => {
  getClientShippingLabelSettings();
});
</script>

<style>
.parcel-config-options {
  margin: 0;
  padding: 0;
  border: none;
}
</style>
